'use client';
import { useEffect, useState } from 'react';
import Link from 'next/link';
import Navigation from '@/components/common/Navigation';
import Footer from '@/components/common/Footer';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface CareerFormData {
  fullName: string;
  email: string;
  phone: string;
  position: string;
  experience: string;
  message: string;
}

const initialFormData: CareerFormData = {
  fullName: '',
  email: '',
  phone: '',
  position: '',
  experience: '',
  message: '',
};

export default function CareersPage() {
  const [formData, setFormData] = useState<CareerFormData>(initialFormData);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals?.length) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals?.forEach(el => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const supabase = createClient();
      const { error: supabaseError } = await supabase
        .from('careers_applications')
        .insert({
          full_name: formData.fullName,
          email: formData.email,
          phone: formData.phone || null,
          position: formData.position,
          experience: formData.experience || null,
          message: formData.message || null,
        });

      if (supabaseError) {
        setError('There was an error submitting your application. Please try again.');
        console.error('Careers form error:', supabaseError.message);
      } else {
        setSubmitted(true);
        setFormData(initialFormData);
      }
    } catch (err: any) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Careers form exception:', err?.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-background">
      <Navigation />

      {/* Page Header */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 border-b border-border bg-card/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-2 mb-6">
            <Link href="/homepage" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              Home
            </Link>
            <Icon name="ChevronRightIcon" size={16} className="text-muted-foreground" />
            <span className="text-sm text-foreground">Careers</span>
          </div>
          <span className="text-xs font-mono uppercase tracking-widest text-primary mb-4 block reveal">
            Join Our Team
          </span>
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-foreground mb-6 reveal">
            Build the Infrastructure
            <br />
            <span className="text-gradient-orange">That Powers America.</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl reveal" style={{ transitionDelay: '200ms' }}>
            East Coast Underground is growing. We&apos;re looking for skilled operators, project managers, and field technicians
            who take pride in doing the job right — underground, on time, and under budget.
          </p>
        </div>
      </section>

      {/* Careers Contact Form */}
      <section className="py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-6">
          <div className="mb-12 reveal">
            <span className="text-xs font-mono uppercase tracking-widest text-primary mb-3 block">Apply Today</span>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Career Application</h2>
            <p className="text-muted-foreground leading-relaxed">
              Interested in joining the East Coast Underground team? Fill out the form below and we&apos;ll be in touch.
            </p>
          </div>

          {submitted ? (
            <div className="industrial-border bg-card rounded-xl p-10 text-center reveal">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name="CheckCircleIcon" size={32} className="text-primary" />
              </div>
              <h3 className="text-2xl font-bold text-foreground mb-3">Application Received</h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                Thank you for your interest in joining our team. We&apos;ll review your application and reach out if there&apos;s a good fit.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg text-sm font-bold hover:bg-primary/90 transition-all"
              >
                Submit Another Application
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="industrial-border bg-card rounded-xl p-8 md:p-10 reveal">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Full Name */}
                <div className="flex flex-col gap-2">
                  <label htmlFor="fullName" className="text-sm font-semibold text-foreground">
                    Full Name <span className="text-primary">*</span>
                  </label>
                  <input
                    id="fullName"
                    name="fullName"
                    type="text"
                    required
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder="John Smith"
                    className="bg-background border border-border rounded-lg px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
                  />
                </div>

                {/* Email */}
                <div className="flex flex-col gap-2">
                  <label htmlFor="email" className="text-sm font-semibold text-foreground">
                    Email Address <span className="text-primary">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    className="bg-background border border-border rounded-lg px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
                  />
                </div>

                {/* Phone */}
                <div className="flex flex-col gap-2">
                  <label htmlFor="phone" className="text-sm font-semibold text-foreground">
                    Phone Number
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(555) 000-0000"
                    className="bg-background border border-border rounded-lg px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
                  />
                </div>

                {/* Position */}
                <div className="flex flex-col gap-2">
                  <label htmlFor="position" className="text-sm font-semibold text-foreground">
                    Position of Interest <span className="text-primary">*</span>
                  </label>
                  <select
                    id="position"
                    name="position"
                    required
                    value={formData.position}
                    onChange={handleChange}
                    className="bg-background border border-border rounded-lg px-4 py-3 text-sm text-foreground focus:outline-none focus:border-primary transition-colors"
                  >
                    <option value="">Select a position...</option>
                    <option value="HDD Drill Operator">HDD Drill Operator</option>
                    <option value="Project Manager – Underground Utilities">Project Manager – Underground Utilities</option>
                    <option value="Field Technician – Utility Locating">Field Technician – Utility Locating</option>
                    <option value="Senior Estimator">Senior Estimator</option>
                    <option value="Other">Other / General Inquiry</option>
                  </select>
                </div>
              </div>

              {/* Years of Experience */}
              <div className="flex flex-col gap-2 mb-6">
                <label htmlFor="experience" className="text-sm font-semibold text-foreground">
                  Years of Relevant Experience
                </label>
                <select
                  id="experience"
                  name="experience"
                  value={formData.experience}
                  onChange={handleChange}
                  className="bg-background border border-border rounded-lg px-4 py-3 text-sm text-foreground focus:outline-none focus:border-primary transition-colors"
                >
                  <option value="">Select experience level...</option>
                  <option value="0-1 years">0–1 years</option>
                  <option value="2-4 years">2–4 years</option>
                  <option value="5-9 years">5–9 years</option>
                  <option value="10+ years">10+ years</option>
                </select>
              </div>

              {/* Message */}
              <div className="flex flex-col gap-2 mb-8">
                <label htmlFor="message" className="text-sm font-semibold text-foreground">
                  Tell Us About Yourself
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Briefly describe your background, certifications, and why you want to join East Coast Underground..."
                  className="bg-background border border-border rounded-lg px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors resize-none"
                />
              </div>

              {error && (
                <div className="mb-6 flex items-center gap-3 bg-destructive/10 border border-destructive/30 rounded-lg px-4 py-3">
                  <Icon name="ExclamationCircleIcon" size={18} className="text-destructive flex-shrink-0" />
                  <p className="text-sm text-destructive">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-lg font-bold text-base hover:bg-primary/90 transition-all disabled:opacity-60 disabled:cursor-not-allowed group"
              >
                {submitting ? (
                  <>
                    <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit Application
                    <Icon name="ArrowRightIcon" size={20} className="group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
}
